<template>
  <div>
    <transition name="modal">
      <transition-modal
        v-if="modalMode"
        :mode="modalMode"
        :loading="loadingAction"
        @close="closeModal"
        @confirm="onModalConfirm"
      />
    </transition>

    <div v-if="!loading">
      <div v-if="order" class="flex flex-col">
        <div class="flex items-center space-x-2">
          <router-link :to="{ name: 'shopOrdersIndex' }" class="system-button">
            <arrow-icon class="w-4 rotate-90"></arrow-icon>
          </router-link>
        </div>

        <div class="card mt-2">
          <div class="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 class="font-mono">Pedido #{{ order.id }}</h2>
              <p class="text-content-400">{{ formatDatetime(order.created_at) }}</p>
            </div>
            <span
              :class="[
                'p-1 text-xs rounded text-white h-fit',
                statusColor(order.status),
              ]"
            >{{ statusLabel(order.status) }}</span>
          </div>

          <div class="div-line"></div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 class="font-medium">Cliente</h3>
              <div class="text-sm mt-2">
                <p v-if="order.client">
                  <router-link
                    :to="{ name: 'clientsDetail', params: { id: order.client.id } }"
                    class="underline system-button"
                  >
                    {{ order.client.name }} {{ order.client.last_name }}
                  </router-link>
                </p>
                <p v-else>
                  {{ order.guest_name }}
                  <span class="text-xs bg-content-100 text-content-400 px-1 rounded ml-1">
                    invitado
                  </span>
                </p>
                <p class="text-content-300">
                  {{ order.client?.user?.email || order.guest_email }}
                </p>
                <p class="text-content-300" v-if="order.guest_phone || order.client?.phone">
                  {{ order.guest_phone || order.client?.phone }}
                </p>
              </div>
            </div>

            <div>
              <h3 class="font-medium">Envío</h3>
              <div class="text-sm mt-2">
                <p>{{ order.shipping_recipient_name }}</p>
                <p class="text-content-400">{{ order.shipping_phone }}</p>
                <p>
                  {{ order.shipping_street }}
                  {{ order.shipping_exterior_number }}
                  <span v-if="order.shipping_interior_number">int. {{ order.shipping_interior_number }}</span>
                </p>
                <p>{{ order.shipping_neighborhood }}</p>
                <p>{{ order.shipping_city }}, {{ order.shipping_state }} {{ order.shipping_zip_code }}</p>
                <p v-if="order.shipping_notes" class="text-content-400 italic mt-1">
                  Notas: {{ order.shipping_notes }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="card mt-4">
          <h3 class="font-medium">Productos</h3>

          <table class="table-auto text-sm mt-2 bg-white w-full">
            <thead class="text-left">
              <tr class="bg-primary-100">
                <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Producto</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">SKU</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cantidad</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">P. Unitario</th>
                <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in order.items" :key="item.id" class="table-row">
                <td class="table-cell">
                  {{ item.product_name_snapshot }}
                  <span v-if="item.variant_name_snapshot" class="text-content-300">
                    — {{ item.variant_name_snapshot }}
                  </span>
                </td>
                <td class="table-cell font-mono text-xs">{{ item.product_sku_snapshot }}</td>
                <td class="table-cell">{{ item.quantity }}</td>
                <td class="table-cell">{{ integerToCurrency(item.unit_price) }}</td>
                <td class="table-cell">{{ integerToCurrency(item.line_total) }}</td>
              </tr>
            </tbody>
          </table>

          <div class="flex flex-col items-end mt-4 text-sm space-y-1">
            <div class="flex justify-between w-full sm:w-72">
              <span>Subtotal:</span>
              <span>{{ integerToCurrency(order.subtotal) }}</span>
            </div>
            <div class="flex justify-between w-full sm:w-72">
              <span>Envío:</span>
              <span>{{ integerToCurrency(order.shipping_price) }}</span>
            </div>
            <div class="flex justify-between w-full sm:w-72">
              <span>IVA:</span>
              <span>{{ integerToCurrency(order.tax) }}</span>
            </div>
            <div v-if="Number(order.discount) > 0" class="flex justify-between w-full sm:w-72 text-success-500">
              <span>Descuento:</span>
              <span>-{{ integerToCurrency(order.discount) }}</span>
            </div>
            <div class="flex justify-between w-full sm:w-72 font-semibold text-base mt-1 border-t pt-1">
              <span>Total:</span>
              <span>{{ integerToCurrency(order.total) }}</span>
            </div>
            <!-- Monedero = medio de pago (F7): reduce el cargo a tarjeta, no el total. -->
            <div v-if="Number(order.wallet_applied) > 0" class="flex justify-between w-full sm:w-72 text-success-500">
              <span>Monedero:</span>
              <span>-{{ integerToCurrency(order.wallet_applied) }}</span>
            </div>
            <div v-if="Number(order.wallet_applied) > 0" class="flex justify-between w-full sm:w-72 font-semibold">
              <span>Pagado con tarjeta:</span>
              <span>{{ integerToCurrency(Number(order.total) - Number(order.wallet_applied)) }}</span>
            </div>
          </div>
        </div>

        <div class="card mt-4">
          <h3 class="font-medium">Pago</h3>
          <div class="text-sm mt-2 space-y-1">
            <div>
              <span class="font-medium">PaymentIntent:</span>
              <span class="font-mono ml-2">{{ order.stripe_payment_intent_id || "—" }}</span>
              <a
                v-if="order.stripe_payment_intent_id"
                :href="stripeDashboardUrl"
                target="_blank"
                rel="noopener"
                class="underline system-button ml-2"
              >Ver en Stripe</a>
            </div>
            <div v-if="order.coupon">
              <span class="font-medium">Cupón:</span>
              <span class="ml-2 font-mono">{{ order.coupon.code }}</span>
              <span class="text-content-300 ml-2">— {{ order.coupon.name }}</span>
            </div>
          </div>
        </div>

        <div v-if="order.needs_invoice" class="card mt-4">
          <h3 class="font-medium">Factura</h3>
          <div class="text-sm mt-2 space-y-1">
            <p><span class="font-medium">RFC:</span> {{ order.invoice_rfc || "—" }}</p>
            <p><span class="font-medium">Razón social:</span> {{ order.invoice_business_name || "—" }}</p>
            <p><span class="font-medium">Régimen fiscal:</span> {{ taxRegimeLabel(order.invoice_tax_regime) }}</p>
            <p><span class="font-medium">Uso CFDI:</span> {{ cfdiUseLabel(order.invoice_use_cfdi) }}</p>
            <p><span class="font-medium">Código postal fiscal:</span> {{ order.invoice_zip_code || "—" }}</p>
            <p><span class="font-medium">Email:</span> {{ order.invoice_email || "—" }}</p>
            <div v-if="order.billing_street" class="mt-2">
              <p class="font-medium">Dirección fiscal</p>
              <p>
                {{ order.billing_street }}
                {{ order.billing_exterior_number }}
                <span v-if="order.billing_interior_number">int. {{ order.billing_interior_number }}</span>
              </p>
              <p>{{ order.billing_neighborhood }}</p>
              <p>{{ order.billing_city }}, {{ order.billing_state }} {{ order.billing_zip_code }}</p>
            </div>
          </div>
        </div>

        <div v-if="order.tracking_number" class="card mt-4">
          <h3 class="font-medium">Rastreo</h3>
          <div class="text-sm mt-2 space-y-1">
            <p><span class="font-medium">Guía:</span> {{ order.tracking_number }}</p>
            <p v-if="order.tracking_url">
              <span class="font-medium">URL:</span>
              <a
                :href="order.tracking_url"
                target="_blank"
                rel="noopener"
                class="underline system-button ml-2"
              >{{ order.tracking_url }}</a>
            </p>
          </div>
        </div>

        <div v-if="hasPermission('edit_shop_orders')" class="card mt-4">
          <h3 class="font-medium">Acciones</h3>

          <div class="flex flex-wrap gap-2 mt-3">
            <button
              v-if="order.status === 'pending' && order.stripe_payment_intent_id"
              class="primary-button mx-0"
              :disabled="verifying"
              @click="verifyPayment"
            >{{ verifying ? "Verificando…" : "Verificar pago" }}</button>

            <button
              v-if="canTransition('packing')"
              class="primary-button mx-0"
              @click="openModal('packing')"
            >Marcar empacada</button>

            <button
              v-if="canTransition('shipped')"
              class="primary-button mx-0"
              @click="openModal('shipped')"
            >Marcar enviada</button>

            <button
              v-if="canTransition('delivered')"
              class="primary-button mx-0"
              @click="openModal('delivered')"
            >Marcar entregada</button>

            <button
              v-if="canTransition('cancelled')"
              class="secondary-button"
              @click="openModal('cancelled')"
            >Cancelar</button>

            <button
              v-if="canTransition('refunded')"
              class="secondary-button"
              @click="openModal('refunded')"
            >Reembolsar</button>

            <button
              v-if="['paid', 'packing', 'shipped', 'delivered'].includes(order.status)"
              class="secondary-button"
              :disabled="resending"
              @click="resendConfirmation"
            >{{ resending ? "Enviando…" : "Reenviar confirmación" }}</button>
          </div>

          <p
            v-if="!hasAnyAction"
            class="text-content-300 text-sm mt-2"
          >Esta orden está en un estado terminal.</p>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
import TransitionModal from "./TransitionModal.vue";

const TRANSITIONS = {
  pending: ["cancelled"],
  paid: ["packing", "cancelled", "refunded"],
  packing: ["shipped", "cancelled"],
  shipped: ["delivered", "cancelled", "refunded"],
  delivered: ["refunded"],
  cancelled: [],
  refunded: [],
};

// SAT c_RegimenFiscal (CFDI 4.0) — mirrors website/src/shop/components/CheckoutInvoice.vue.
const TAX_REGIMES = {
  "601": "General de Ley Personas Morales",
  "603": "Personas Morales con Fines no Lucrativos",
  "605": "Sueldos y Salarios e Ingresos Asimilados a Salarios",
  "606": "Arrendamiento",
  "607": "Régimen de Enajenación o Adquisición de Bienes",
  "608": "Demás ingresos",
  "610": "Residentes en el Extranjero sin Establecimiento Permanente",
  "611": "Ingresos por Dividendos (socios y accionistas)",
  "612": "Personas Físicas con Actividades Empresariales y Profesionales",
  "614": "Ingresos por intereses",
  "615": "Régimen de los ingresos por obtención de premios",
  "616": "Sin obligaciones fiscales",
  "620": "Sociedades Cooperativas de Producción que difieren ingresos",
  "621": "Incorporación Fiscal",
  "622": "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
  "623": "Opcional para Grupos de Sociedades",
  "624": "Coordinados",
  "625": "Actividades Empresariales con ingresos por Plataformas Tecnológicas",
  "626": "Régimen Simplificado de Confianza (RESICO)",
};

// SAT c_UsoCFDI (CFDI 4.0) — mirrors the checkout form's options.
const CFDI_USES = {
  G01: "Adquisición de mercancías",
  G03: "Gastos en general",
  I01: "Construcciones",
  I02: "Mobiliario y equipo de oficina por inversiones",
  I03: "Equipo de transporte",
  I04: "Equipo de cómputo y accesorios",
  I08: "Otra maquinaria y equipo",
  D01: "Honorarios médicos, dentales y gastos hospitalarios",
  S01: "Sin efectos fiscales",
  CP01: "Pagos",
};

export default {
  name: "Shop Order Detail",

  components: { TransitionModal },

  data() {
    return {
      order: null,
      loading: false,
      modalMode: null,
      loadingAction: false,
      verifying: false,
      resending: false,
    };
  },

  computed: {
    stripeDashboardUrl() {
      if (!this.order?.stripe_payment_intent_id) return "#";
      return `https://dashboard.stripe.com/payments/${this.order.stripe_payment_intent_id}`;
    },

    hasAnyAction() {
      if (!this.order) return false;
      return (TRANSITIONS[this.order.status] || []).length > 0;
    },
  },

  created() {
    this.$store.commit("setStoreSelectedTab", "shopOrders");
    this.loadOrder();
  },

  methods: {
    formatDatetime,
    integerToCurrency,

    taxRegimeLabel(code) {
      if (!code) return "—";
      return TAX_REGIMES[code] ? `${code} - ${TAX_REGIMES[code]}` : code;
    },

    cfdiUseLabel(code) {
      if (!code) return "—";
      return CFDI_USES[code] ? `${code} - ${CFDI_USES[code]}` : code;
    },

    statusLabel(status) {
      const map = {
        pending: "Pendiente",
        paid: "Pagada",
        packing: "Empacando",
        shipped: "Enviada",
        delivered: "Entregada",
        cancelled: "Cancelada",
        refunded: "Reembolsada",
      };
      return map[status] || status;
    },

    statusColor(status) {
      const map = {
        pending: "bg-content-300",
        paid: "bg-success-500",
        packing: "bg-orange-500",
        shipped: "bg-primary-500",
        delivered: "bg-success-700",
        cancelled: "bg-red-500",
        refunded: "bg-content-400",
      };
      return map[status] || "bg-content-300";
    },

    canTransition(next) {
      if (!this.order) return false;
      return (TRANSITIONS[this.order.status] || []).includes(next);
    },

    openModal(mode) {
      this.modalMode = mode;
    },

    closeModal() {
      this.modalMode = null;
    },

    async onModalConfirm(payload) {
      if (!this.modalMode) return;
      this.loadingAction = true;

      try {
        if (this.modalMode === "shipped") {
          const response = await api.shopOrders.markShipped(this.order.id, payload);
          this.$store.dispatch("successSnack", response.message);
        } else if (this.modalMode === "refunded") {
          const response = await api.shopOrders.refund(this.order.id);
          this.$store.dispatch("successSnack", response.message);
        } else {
          const response = await api.shopOrders.transition(this.order.id, this.modalMode);
          this.$store.dispatch("successSnack", response.message);
        }

        this.closeModal();
        this.loadOrder();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAction = false;
    },

    async resendConfirmation() {
      const fallback =
        this.order.guest_email || this.order.client?.user?.email || "";
      const email = window.prompt("Reenviar detalles del pedido a:", fallback);
      if (email === null) return; // cancelled
      const trimmed = email.trim();
      if (!trimmed) {
        this.$store.dispatch("errorSnack", "Ingresa un correo válido.");
        return;
      }

      this.resending = true;
      try {
        const response = await api.shopOrders.resendConfirmation(this.order.id, trimmed);
        this.$store.dispatch("successSnack", response.message);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.resending = false;
    },

    async verifyPayment() {
      this.verifying = true;
      try {
        const response = await api.shopOrders.verifyPayment(this.order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrder();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.verifying = false;
    },

    async loadOrder() {
      this.loading = true;

      try {
        this.order = await api.shopOrders.getById(this.$route.params.id);
        this.$store.commit("setBreadcrumbLabel", {
          routeName: "shopOrdersDetail",
          label: "Pedido #" + this.order.id,
        });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "shopOrdersIndex" });
      }

      this.loading = false;
    },
  },
};
</script>
